import React, { Fragment } from "react";
import { Clock, Plug, X } from "lucide-react";
import styled from "styled-components";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import socket from "../../../services/socket";
import { useSearchParams } from "react-router-dom";

export function LiveLog() {
   const settingsIsRun = useSelector((state: RootState) => state.settings.data.isWorkflowRun);
   const [isToggle, setIsToggle] = React.useState(false);
   const [timer, setTimer] = React.useState('00.000s');
   const [items, setItems] = React.useState<{ type: 'success' | 'failed', time: string, title: string }[]>([]);
   const [searchParams] = useSearchParams();
   const itemsRef = React.useRef<HTMLUListElement>(null);

   // ---
   React.useEffect(() => {
      let interval: NodeJS.Timeout | null = null;
      let startTime: number;

      if (settingsIsRun) {
         setTimer("00.000s");
         startTime = Date.now();
         interval = setInterval(() => {
            const diff = Date.now() - startTime;
            const seconds = Math.floor(diff / 1000);
            const ms = diff % 1000;
            const formatted = `${seconds.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}s`;
            setTimer(formatted);
         }, 10);
      }
      return () => { if (interval) clearInterval(interval) }
   }, [settingsIsRun]);


   // ---
   React.useEffect(() => {
      const nodeExecutedLog = (response: any) => {
         const projectId = searchParams.get('id');
         if (projectId !== response.projectId) return;
         setItems(prev => [...prev, response.data]);
      }
      socket.on('nodeExecutedLog', nodeExecutedLog);
      return () => { socket.off('nodeExecutedLog', nodeExecutedLog) }
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   // ---
   React.useEffect(() => {
      itemsRef.current?.scrollTo(0, itemsRef.current.scrollHeight);
   }, [items]);

   return (<>
      <div className="boxLog liveLog" onClick={ _=> setIsToggle(true) }>
         <div className="icon"><Plug size="16px" /></div>
         <div className="info">
            <span>Live Log</span>
         </div>
      </div>
      { isToggle ? <Popup>
         <div className="close" onClick={ _=> setIsToggle(false) }>
            <X size={ 19 } />
         </div>
         <div className="time" title="Last execution">
            <Clock size={ 15 } />
            { timer }
         </div>
         <ul ref={ itemsRef } className="items">{
            items.length ? items.map((item, index) => (
               <Fragment key={ index.toString() }>
                  { index && item.title === 'workflow started' ? <div className="line"><span>New Log</span></div> : null }
                  <li><span>{ item.time }</span><span className={ item.type }>{ item.title }</span></li>
               </Fragment>
            )) : <div className="empty">There are no logs currently</div>
         }</ul>
      </Popup> : null }
   </>);
}

const Popup = styled.div`
   position: absolute;
   bottom: 0;
   left: 0;
   background-color: #fefefe;
   padding: 15px 20px;
   border-radius: 5px;
   box-shadow: 0 1px 3px rgba(0,0,0,0.12);
   width: 100%;
   height: 135px;
   z-index: 99;

   .time {
      position: absolute;
      display: flex;
      align-items: center;
      font-size: 14px;
      bottom: 100%;
      left: 15px;
      padding: 2.5px 7.5px;
      background-color: #fefefe;
      box-shadow: 0 1px 3px rgba(0,0,0,0.12);
      border-radius: 5px;
      color: rgba(0, 0, 0, 0.5);

      svg {
         margin-right: 5px;
      }
   }
   .close {
      position: absolute;
      bottom: 100%;
      right: 15px;
      padding: 2.5px 5px 1px;
      cursor: pointer;
      background-color: #F6F6F6;
      border: 1px solid rgb(230, 230, 230);
      border-bottom: none;

      svg {
         opacity: 0.6;
         transition: all .2s ease-in-out;
      }
      &:hover svg {
         opacity: 0.85;
      }
   }
   .items {
      height: 100%;
      overflow: auto;

      .empty {
         display: flex;
         align-items: center;
         justify-content: center;
         height: 100%;
         opacity: 0.65;
         font-size: 15px;
      }
      .line {
         display: flex;
         align-items: center;
         margin: 7.5px 0;
         white-space: nowrap;
         font-size: 13px;
         color: rgba(0, 0, 0, 0.35);

         &::before, &::after {
            content: '';
            background-color: rgba(0,0,0,0.15);
            width: 100%;
            height: 1px;
         }
         span {
            margin: 0 7.5px;
         }
      }
      li {
         display: flex;
         align-items: center;

         span:first-of-type {
            font-size: 13px;
            color: rgba(0, 0, 0, 0.35);
         }
         span:last-of-type {
            font-size: 15px;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1);
            border-radius: 5px;
            padding: 2px 15px;
            margin-left: 7.5px;
            color: #fff;
         }
         .success {
            background-image: linear-gradient(to bottom right, #06b6d4, #3b82f6);
         }
         .failed {
            background-image: linear-gradient(to right, #db5757, #d22d2d);
         }
         &:not(:last-of-type) {
            margin-bottom: 7.5px;
         }
      }
   }
`;